import { UIEventPayload } from '@atlassiansox/analytics-web-client';
import { Attributes } from '@atlassiansox/analytics-web-client/dist/types/types';
import { ENVIRONMENT } from '@atlassian/community-library/dist/constants';
import { CommunityAnalyticsClient, eventType } from '@atlassian/community-library';

import { getEnvironment } from 'src/services/http/user';
import { detectPage } from 'src/utils/detectPage';
import { Environment } from 'src/constants';

let communityClientInstance: typeof CommunityAnalyticsClient | null = null;

// Map our Environment enum to the community library's ENVIRONMENT type
const ENVIRONMENT_MAP: Record<Environment, ENVIRONMENT> = {
  [Environment.DEVELOPMENT]: ENVIRONMENT.DEV,
  [Environment.LOCAL]: ENVIRONMENT.LOCAL,
  [Environment.STAGING]: ENVIRONMENT.STAGING,
  [Environment.PRODUCTION]: ENVIRONMENT.PROD,
};

export const checkIfOneTrustIsLoaded = (): boolean => {
  const typedWindow = window as typeof window & {
    OnetrustActiveGroups?: string;
    dataLayer?: unknown[];
  };

  // this check is similar to what Community does before initializing the analytics client
  // https://bitbucket.org/atlassian/community-ui/src/master/src/hooks/useInitializeAnalyticsClient.ts
  const isConsentGroupActive = typedWindow.OnetrustActiveGroups?.includes(',2,');

  // CommunityAnalyticsClient.initWithPageAnalytics initializes Google Tag Manager inside;
  // for some reason, if we do not wait for the consent update event, it won't work properly
  const consentUpdateEvent = typedWindow?.dataLayer?.find(
    (item: unknown) => (item as Array<string>)[0] === 'consent' && (item as Array<string>)[1] === 'update',
  );

  return !!isConsentGroupActive && !!consentUpdateEvent;
};

export const waitForOneTrustToLoad = async (): Promise<void> =>
  new Promise((resolve) => {
    if (checkIfOneTrustIsLoaded()) {
      resolve();

      return;
    }

    const oneTrustCheckIntervalId = setInterval((): void => {
      if (checkIfOneTrustIsLoaded()) {
        clearInterval(oneTrustCheckIntervalId);

        resolve();
      }
    }, 250);
  });

export const getCommunityAnalyticsClient = async (userId?: string): Promise<CommunityAnalyticsClient | null> => {
  if (communityClientInstance) {
    return communityClientInstance;
  }

  await waitForOneTrustToLoad();

  const { CommunityAnalyticsClient } = await import('@atlassian/community-library');

  const env = getEnvironment();

  if (!userId) {
    CommunityAnalyticsClient.getAnalyticsClient()?.clearUserInfo();
  }

  communityClientInstance = CommunityAnalyticsClient;

  CommunityAnalyticsClient.initWithPageAnalytics(
    CommunityAnalyticsClient.SUB_APP.LEARNING,
    {
      useLegacyUrl: env === Environment.DEVELOPMENT,
    },
    ENVIRONMENT_MAP[env],
    undefined,
    userId || null,
  );

  return communityClientInstance;
};

export const sendScreenEvent = async (name: string, attributes: Attributes, userId?: string): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.SCREEN, {
    name,
    attributes,
  });
};

export const sendOperationalEvent = async (
  source: string,
  actionSubject: string,
  action: string,
  attributes: Attributes,
  userId?: string,
): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.OPERATIONAL, {
    source,
    action,
    actionSubject,
    attributes,
  });
};

export const sendTrackEvent = async (
  source: string,
  actionSubject: string,
  action: string,
  attributes: Attributes,
  userId?: string,
): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.TRACK, {
    source,
    action,
    actionSubject,
    attributes,
  });
};

export const sendUIEvent = async (
  source: string,
  actionSubject: string,
  action: string,
  actionSubjectId?: string,
  attributes?: Attributes,
  userId?: string,
): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.UI, {
    source,
    action,
    actionSubject,
    actionSubjectId,
    attributes,
  });
};

export const sendMarketingUIEvent = async (payload: UIEventPayload, userId?: string): Promise<void> => {
  const analyticsCommunityClient = await getCommunityAnalyticsClient(userId);
  const extendedPayload = {
    ...payload,
    attributes: {
      ...payload.attributes,
      navigatedFrom: detectPage(location.pathname),
    },
  };

  (analyticsCommunityClient as typeof CommunityAnalyticsClient).sendEvent(eventType.UI, extendedPayload);
};
